import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/iron-localstorage/iron-localstorage.js';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {FrSharedStylesHTML} from './shared-styles/fr.js';
import {loadDynamicDependency} from '../javascripts/modules/loadDynamicDependency.js';
import {HotkeyBehavior} from './behaviors/hotkey-behavior.js';

class KeliReportEditor extends ScopeBinding(HotkeyBehavior(PolymerElement)) {
  static get template() {
    return html`
${FrSharedStylesHTML}
<style>
  :host {
    display: block;
    overflow: hidden;
    outline: none;
  }
  
  #container {
    height: 100%;
    overflow: hidden;
    position: relative;
  }
  
  .fluentReports {
    height: 100% !important;
  }
</style>

<div id="container"></div>`;
  }

  static get is() {
    return 'keli-report-editor'
  }

  static get properties() {
    return {
      data: {type: Array, scopevar: true, observer: 'dataChanged'},
      displayValue: {type: Object, scopevar: true, observer: 'displayValueChanged'},
      fluentReports: {type: Object},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['reporteditor']},
      preview: {type: Object, scopevar: true, observer: 'previewChanged'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliAccepted']);
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._ensureAttribute('tabindex', '0');
    this.classList.add('focusable-control');

    this.addEventListener('focusElement', this.handleFocusElement);

    await loadDynamicDependency([
      {global: window.FluentReportsGenerator, path: '/javascripts/fluentreports-package.js'},
    ]);

    this.fluentReports = new window.FluentReportsGenerator({
      css: false,
      js: false,
      multiSelectKey: 'shift',
      element: this.$.container,
      report: this.displayValue || undefined,
      data: this.data || undefined,
      blankReport: !!(this.displayValue || this.data),
      preview: this.preview,
      formatterFunctions: {
        'percent': value => value,
        'number': value => value,
        'money': value => value,
        'date': value => value,
        'datetime': value => value,
        'printedAt': value => value,
      },
      save: (value, callback) => {
        this._keliAccept({detail: {reportStructure: value}});
        callback?.();
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.panel.nextField, {
      down: event => {
        event.handledByElement = true;
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.panel.previousField, {
      down: event => {
        event.handledByElement = true;
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.desktop.close, {
      down: event => {
        if (this.fluentReports && this.fluentReports._frame.dialogCount) {
          event.handledByElement = true;
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('focusElement', this.handleFocusElement);
    this.fluentReports = null;
  }

  dataChanged(newValue) {
    if (!this.fluentReports) {
      return;
    }
    this.fluentReports.setConfig('data', newValue);
  }

  displayValueChanged(newValue) {
    if (!this.fluentReports) {
      return;
    }
    if (newValue && typeof newValue === 'string') {
      newValue = JSON.parse(newValue);
    }
    this.fluentReports.setConfig('report', newValue);
  }

  previewChanged(newValue) {
    if (!this.fluentReports) {
      return;
    }
    this.fluentReports.setConfig('preview', newValue);
  }

  handleFocusElement() {
    this.focus();
  }

  _keliAccept(event) {
    let newValue = event?.detail?.reportStructure;
    // Do not save if there are dialogs open.
    if (this.fluentReports && this.fluentReports._frame.dialogCount) {
      return;
    }

    if (!newValue && this.fluentReports) {
      newValue = this.fluentReports._generateSave();
    }

    let oldValue = this.displayValue;
    if (oldValue && typeof oldValue === 'string') {
      oldValue = JSON.parse(oldValue);
    }

    if (newValue && (!oldValue || !this.deepEqual(newValue, oldValue))) {
      this.displayValue = newValue;
      this.data = this.fluentReports.data;
      this.dispatchEvent(new CustomEvent('keliAccepted', {detail: {element: this, value: newValue}}));
    }
  }

  deepEqual(object1, object2) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      const val1 = object1[key];
      const val2 = object2[key];
      const areObjects = this.isObject(val1) && this.isObject(val2);
      if (
        areObjects && !this.deepEqual(val1, val2) ||
        !areObjects && val1 !== val2
      ) {
        return false;
      }
    }

    return true;
  }

  isObject(object) {
    return object != null && typeof object === 'object';
  }
}

customElements.define(KeliReportEditor.is, KeliReportEditor);
export {KeliReportEditor};
